import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { FaEdit } from 'react-icons/fa';

const StoreBatchModal = ({ show, onClose, selectedProduct, onBatchUpdate }) => {
  const { user, token } = useAuth();
  const [purchaseBatches, setPurchaseBatches] = useState([]);
  const [batchEdits, setBatchEdits] = useState({});
  const [savingBatchId, setSavingBatchId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cost update modal state
  const [showCostModal, setShowCostModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [enteredPrice, setEnteredPrice] = useState('');
  const [costNote, setCostNote] = useState('');
  const [isUpdatingCost, setIsUpdatingCost] = useState(false);

  const API_BASE = '/api';

  // Fetch purchase batches when product changes
  useEffect(() => {
    if (selectedProduct?.id) {
      fetchPurchaseBatches(selectedProduct.id);
    } else {
      setPurchaseBatches([]);
      setBatchEdits({});
    }
  }, [selectedProduct]);

  const fetchPurchaseBatches = async (productId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/store-products/batch/${productId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Owner-Id': String(user?.ownerId || user?.id || ''),
          'X-Staff-Id': String(user?.staffId ?? '')
        }
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      const filtered = (Array.isArray(data) ? data : []).filter(batch => batch.quantity_remaining > 0);
      setPurchaseBatches(filtered);

      // Initialize batch edits with current values
      const init = {};
      filtered.forEach(b => {
        init[b.id] = {
          first_price: b.first_price ?? '',
          selling_price: b.selling_price ?? '',
          barcode: b.barcode ?? ''
        };
      });
      setBatchEdits(init);
    } catch (error) {
      setError('Failed to fetch purchase batches');
      console.error('Error fetching purchase batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBatchEditField = (batchId, field, value) => {
    setBatchEdits(prev => ({
      ...prev,
      [batchId]: {
        ...(prev[batchId] || {}),
        [field]: value
      }
    }));
  };

  const handleSaveBatch = async (batch) => {
    try {
      setSavingBatchId(batch.id);
      const edit = batchEdits[batch.id] || {};
      const payload = {};
      if (edit.first_price !== undefined && edit.first_price !== '') payload.first_price = Number(edit.first_price);
      if (edit.selling_price !== undefined && edit.selling_price !== '') payload.selling_price = Number(edit.selling_price);
      if (selectedProduct?.costing_method === 'Specific' && edit.barcode !== undefined) {
        payload.barcode = String(edit.barcode).trim();
      }

      const response = await fetch(`${API_BASE}/store-products/batch/${batch.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Owner-Id': String(user?.ownerId || user?.id || ''),
          'X-Staff-Id': String(user?.staffId ?? '')
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Server error' }));
        throw new Error(err.message || 'Server error');
      }
      const updated = await response.json();

      // Update local state
      setPurchaseBatches(prev => prev.map(b => b.id === batch.id ? {
        ...b,
        first_price: updated.first_price,
        selling_price: updated.selling_price,
        barcode: updated.barcode
      } : b));

      // Notify parent component of update
      if (onBatchUpdate) {
        onBatchUpdate(batch.id, updated);
      }
    } catch (e) {
      alert(e.message || 'Failed to save changes');
    } finally {
      setSavingBatchId(null);
    }
  };

  const handleOpenCostModal = (batch) => {
    setEditingBatch(batch);
    setEnteredPrice(batch.cost_per_unit || '');
    setCostNote('');
    setShowCostModal(true);
  };

  const handleUpdateCostPrice = async () => {
    if (!editingBatch) return;

    const ep = Number(enteredPrice);
    if (isNaN(ep)) {
      alert('Iltimos, to\'g\'ri narx kiriting');
      return;
    }

    // Calculation: quantity_purchased * (costperunit - entered_price_by_user)
    const difference = Number(editingBatch.quantity_purchased) * (Number(editingBatch.cost_per_unit) - ep);

    setIsUpdatingCost(true);
    try {
      const response = await fetch(`${API_BASE}/store-products/batch/update-cost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Owner-Id': String(user?.ownerId || user?.id || ''),
        },
        body: JSON.stringify({
          purchase_batch_id: editingBatch.id,
          entered_price: ep,
          difference: difference,
          note: costNote,
          product_id: selectedProduct?.id
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Xatolik yuz berdi');
      }

      // Refresh batches
      fetchPurchaseBatches(selectedProduct.id);
      setShowCostModal(false);

      if (onBatchUpdate) {
        onBatchUpdate();
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUpdatingCost(false);
    }
  };

  const handleClose = () => {
    setPurchaseBatches([]);
    setBatchEdits({});
    setError(null);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Xarid partiyalar: {selectedProduct?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Yuklanmoqda...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-sm table-hover border">
              <thead>
                <tr>
                  <th className="border px-2 py-1">ID</th>
                  <th className="border px-2 py-1">Mahsulot</th>
                  <th className="border px-2 py-1">Ta'minlovchi</th>
                  <th className="border px-2 py-1">Sotib olingan</th>
                  <th className="border px-2 py-1">Tan narx</th>
                  <th className="border px-2 py-1">Ulgurchi narx</th>
                  <th className="border px-2 py-1">Sotish narx</th>
                  <th className="border px-2 py-1">Mavjud miqdor</th>
                  <th className="border px-2 py-1">Valyuta</th>
                  <th className="border px-2 py-1">Ombor</th>
                  <th className="border px-2 py-1">Barcode</th>
                  <th className="border px-2 py-1">Sana</th>
                  <th className="border px-2 py-1">Amallar</th>
                </tr>
              </thead>
              <tbody>
                {purchaseBatches.map((batch) => (
                  <tr key={batch.id}>
                    <td className="border px-2 py-1">{batch.id}</td>
                    <td className="border px-2 py-1">{batch.product_name}</td>
                    <td className="border px-2 py-1">{batch.supplier_name}</td>
                    <td className="border px-2 py-1">{batch.quantity_purchased}</td>
                    <td className="border px-2 py-1">
                      {batch.cost_per_unit}
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2 text-warning"
                        onClick={() => handleOpenCostModal(batch)}
                      >
                        <FaEdit />
                      </Button>
                    </td>
                    <td className="border px-2 py-1" style={{ minWidth: '120px' }}>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={batchEdits[batch.id]?.first_price ?? ''}
                        onChange={(e) => updateBatchEditField(batch.id, 'first_price', e.target.value)}
                      />
                    </td>
                    <td className="border px-2 py-1" style={{ minWidth: '120px' }}>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={batchEdits[batch.id]?.selling_price ?? ''}
                        onChange={(e) => updateBatchEditField(batch.id, 'selling_price', e.target.value)}
                      />
                    </td>
                    <td className="border px-2 py-1">{batch.quantity_remaining}</td>
                    <td className="border px-2 py-1">{batch.currency}</td>
                    <td className="border px-2 py-1">{batch.store_name}</td>
                    <td className="border px-2 py-1" style={{ minWidth: '160px' }}>
                      {selectedProduct?.costing_method === 'Specific' ? (
                        <Form.Control
                          type="text"
                          size="sm"
                          value={batchEdits[batch.id]?.barcode ?? ''}
                          onChange={(e) => updateBatchEditField(batch.id, 'barcode', e.target.value)}
                          placeholder="Barcode"
                        />
                      ) : (
                        <span>{batch.barcode || '-'}</span>
                      )}
                    </td>
                    <td className="border px-2 py-1">{new Date(batch.created_at).toLocaleDateString()}</td>
                    <td className="border px-2 py-1" style={{ minWidth: '120px' }}>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleSaveBatch(batch)}
                        disabled={savingBatchId === batch.id}
                      >
                        {savingBatchId === batch.id ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : 'Saqlash'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {purchaseBatches.length === 0 && !loading && !error && (
          <div className="text-muted mt-4 text-center">No purchase batches available for this product</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
      </Modal.Footer>

      {/* Cost Price Update Modal */}
      <Modal show={showCostModal} onHide={() => setShowCostModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Tan narxni tahrirlash</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Yangi tan narx</Form.Label>
            <Form.Control
              type="number"
              value={enteredPrice}
              onChange={(e) => setEnteredPrice(e.target.value)}
              placeholder="Narxni kiriting"
            />
          </Form.Group>

          {editingBatch && enteredPrice !== '' && !isNaN(Number(enteredPrice)) && (
            <div className="mb-3">
              <strong>Farq: </strong>
              <span style={{
                color: (Number(editingBatch.quantity_purchased) * (Number(editingBatch.cost_per_unit) - Number(enteredPrice))) < 0 ? 'red' : 'green',
                fontWeight: 'bold'
              }}>
                {(Number(editingBatch.quantity_purchased) * (Number(editingBatch.cost_per_unit) - Number(enteredPrice))).toFixed(2)} {editingBatch.currency}
              </span>
              <div className="text-muted small">
                {editingBatch.quantity_purchased} * ({editingBatch.cost_per_unit} - {enteredPrice}) = {(Number(editingBatch.quantity_purchased) * (Number(editingBatch.cost_per_unit) - Number(enteredPrice))).toFixed(2)}
              </div>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Izoh (Note)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={costNote}
              onChange={(e) => setCostNote(e.target.value)}
              placeholder="O'zgartirish sababini yozing..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCostModal(false)}>Bekor qilish</Button>
          <Button
            variant="primary"
            onClick={handleUpdateCostPrice}
            disabled={isUpdatingCost || !enteredPrice}
          >
            {isUpdatingCost ? 'Saqlanmoqda...' : 'Saqlash'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default StoreBatchModal;
